import { useTranslation } from "@bounce/i18n"

import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

type UseStoreReservationTextProps = Pick<
  NearbyStorageSpot,
  "last7dReservationCount" | "last30dReservationCount" | "last365dReservationCount"
>

enum ReservationCountPeriod {
  WEEK = "7d",
  MONTH = "30d",
  YEAR = "365d",
}

type ReservationCount = {
  count: number
  period: ReservationCountPeriod
}

export const useReservationCountText = ({
  last7dReservationCount = 0,
  last30dReservationCount = 0,
  last365dReservationCount = 0,
}: UseStoreReservationTextProps) => {
  const { t } = useTranslation()

  const selectedReservation: ReservationCount | undefined = [
    { count: last7dReservationCount, period: ReservationCountPeriod.WEEK },
    { count: last30dReservationCount, period: ReservationCountPeriod.MONTH },
    { count: last365dReservationCount, period: ReservationCountPeriod.YEAR },
  ].find(({ count }) => count > 0 && count >= 10)

  if (!selectedReservation) {
    return null
  }

  const { count, period } = selectedReservation

  switch (period) {
    case ReservationCountPeriod.WEEK:
      return t(
        "cmp.storeCard.reservationsPerWeekLabel",
        "{count, plural, =1 {# booking in the last week} other {# bookings in the last week}}",
        {
          count,
        },
      )
    case ReservationCountPeriod.MONTH:
      return t(
        "cmp.storeCard.reservationsPerMonthLabel",
        "{count, plural, =1 {# booking in the last month} other {# bookings in the last month}}",
        {
          count,
        },
      )
    case ReservationCountPeriod.YEAR:
      return t(
        "cmp.storeCard.reservationsPerYearLabel",
        "{count, plural, =1 {# booking in the last year} other {# bookings in the last year}}",
        {
          count,
        },
      )
    default:
      return null
  }
}
